import { useState } from 'react';
import { Eye, EyeOff, Loader2, Landmark, ShieldAlert, Copy, Check, FileSignature } from 'lucide-react';
import { formatUsd } from '@ach/shared';

import { api, ApiError } from '../lib/api.js';
import { useAdmin } from './useAdminSession.js';
import { WarningNote, ErrorNote } from './components.js';
import type { SubmissionSummary } from './RequestDetail.js';

interface Revealed {
  bankName: string;
  accountType: 'checking' | 'savings';
  accountHolderName: string | null;
  routingNumber: string;
  accountNumber: string;
  revealedBy: string;
  revealedAt: string;
}

export function BankReveal({
  submission,
  customerName,
}: {
  submission: SubmissionSummary;
  customerName: string;
}) {
  const { me } = useAdmin();
  const [revealed, setRevealed] = useState<Revealed | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function reveal() {
    setBusy(true);
    setError(null);
    try {
      setRevealed(await api.get<Revealed>(`/api/admin/submissions/${submission.id}/bank`));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not read those details.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card overflow-hidden border-gold-200">
      <h2 className="flex items-center gap-2 border-b border-slate-100 bg-gold-50 px-4 py-3 font-semibold text-slate-900">
        <Landmark className="h-4 w-4 text-gold-700" aria-hidden />
        Bank details
        <span className="ml-auto text-sm font-normal text-slate-600">
          {formatUsd(submission.amountCents)} authorized
        </span>
      </h2>

      <div className="space-y-4 p-4">
        <dl className="grid gap-x-6 gap-y-1.5 text-sm sm:grid-cols-2">
          <Pair label="Bank" value={submission.bankName} />
          <Pair
            label="Account type"
            value={submission.accountType === 'savings' ? 'Savings' : 'Checking'}
          />
          <Pair label="Name on account" value={submission.accountHolderName ?? '—'} />
          <Pair label="Account" value={`••••${submission.accountLast4}`} mono />
        </dl>

        {revealed ? (
          <div className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <Secret label="Routing number" value={revealed.routingNumber} />
              <Secret label="Account number" value={revealed.accountNumber} />
            </div>
            <div className="flex items-center gap-2">
              <button className="btn-secondary" onClick={() => setRevealed(null)}>
                <EyeOff className="h-4 w-4" aria-hidden />
                Hide
              </button>
              <p className="text-xs text-slate-500">
                Shown to {revealed.revealedBy} at{' '}
                {new Date(revealed.revealedAt).toLocaleTimeString()}. This was written to the access
                log.
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            {error && <ErrorNote>{error}</ErrorNote>}

            {me.canRevealBankDetails ? (
              <>
                <button className="btn-primary" onClick={() => void reveal()} disabled={busy}>
                  {busy ? (
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                  ) : (
                    <Eye className="h-4 w-4" aria-hidden />
                  )}
                  Show full bank details
                </button>
                <p className="text-xs text-slate-500">
                  Your name, the time and your IP address are recorded in the access log whenever
                  these are shown.
                </p>
              </>
            ) : (
              <WarningNote>
                <div className="flex items-start gap-2">
                  <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                  <div>
                    This session signed in without your authenticator code, so it cannot show bank
                    details. Sign out and back in with the code to see them.
                  </div>
                </div>
              </WarningNote>
            )}
          </div>
        )}

        <details className="rounded-lg border border-slate-200 bg-slate-50 p-3">
          <summary className="flex cursor-pointer items-center gap-2 text-sm font-medium text-slate-800">
            <FileSignature className="h-4 w-4 text-slate-400" aria-hidden />
            ACH authorization record (version {submission.authorizationVersion})
          </summary>
          <div className="mt-3 space-y-3 text-sm">
            <dl className="grid gap-x-6 gap-y-1.5 sm:grid-cols-2">
              <Pair label="Signed by" value={submission.authorizationSignatureName} />
              <Pair
                label="Accepted at"
                value={new Date(submission.authorizationAcceptedAt).toLocaleString()}
              />
              <Pair label="From IP" value={submission.submittedIp ?? '—'} mono />
              <Pair label="Customer" value={customerName} />
            </dl>
            {submission.submittedUserAgent && (
              <p className="break-all text-xs text-slate-500">{submission.submittedUserAgent}</p>
            )}
            <div className="whitespace-pre-wrap rounded border border-slate-200 bg-white p-3 text-xs leading-relaxed text-slate-700">
              {submission.authorizationBody}
            </div>
            <p className="text-xs text-slate-500">
              This is the exact wording shown to the customer, kept by version so it can be
              reproduced later. NACHA requires a retained authorization for a WEB debit.
            </p>
          </div>
        </details>
      </div>
    </section>
  );
}

function Pair({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex gap-2">
      <dt className="text-slate-500">{label}</dt>
      <dd className={`text-slate-800 ${mono ? 'font-account' : ''}`}>{value}</dd>
    </div>
  );
}

/**
 * A revealed number, with a copy button.
 *
 * The copy button exists because the alternative is retyping a nine- or
 * seventeen-digit number into the bank's portal, and a transcription error
 * there produces a returned entry and a fee.
 */
function Secret({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  return (
    <div className="rounded-lg border border-gold-200 bg-gold-50 p-3">
      <p className="text-xs font-medium uppercase tracking-wide text-slate-600">{label}</p>
      <div className="mt-1 flex items-center gap-2">
        <code className="font-account text-lg text-slate-900">{value}</code>
        <button
          type="button"
          onClick={() => void copy()}
          className="ml-auto rounded p-1.5 text-slate-500 transition hover:bg-white hover:text-slate-800"
          title={`Copy the ${label.toLowerCase()}`}
        >
          {copied ? (
            <Check className="h-4 w-4 text-emerald-600" aria-label="Copied" />
          ) : (
            <Copy className="h-4 w-4" aria-label="Copy" />
          )}
        </button>
      </div>
    </div>
  );
}
